import { useEffect, useRef } from 'react';
import katex from 'katex';
import type { EquationDefinition, ParameterValues } from '@axlabs/simulation-core';
import styles from './EquationDisplay.module.css';

interface EquationDisplayProps {
  equations: readonly EquationDefinition[];
  parameters: ParameterValues;
  state: Record<string, number>;
}

export function EquationDisplay({ equations, parameters, state }: EquationDisplayProps) {
  // Filter equations based on conditions
  const activeEquations = equations.filter(eq => {
    if (!eq.condition) return true;
    return eq.condition(parameters);
  });
  
  return (
    <div className={styles.container}>
      {activeEquations.map(eq => (
        <EquationItem
          key={eq.id}
          equation={eq}
          parameters={parameters}
          state={state}
        />
      ))}
    </div>
  );
}

interface EquationItemProps {
  equation: EquationDefinition;
  parameters: ParameterValues;
  state: Record<string, number>;
}

function EquationItem({ equation, parameters, state }: EquationItemProps) {
  const mathRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!mathRef.current) return;
    
    try {
      katex.render(equation.latex, mathRef.current, {
        displayMode: true,
        throwOnError: false,
        strict: false,
      });
    } catch (e) {
      console.error('KaTeX render error:', e);
      mathRef.current.textContent = equation.latex;
    }
  }, [equation.latex]);
  
  // Get current values for variables
  const variableValues = equation.variables.map(varId => {
    const value = state[varId] ?? parameters[varId];
    return { id: varId, value };
  }).filter(v => v.value !== undefined);
  
  return (
    <div className={styles.equation}>
      <div className={styles.header}>
        <span className={styles.name}>{equation.name}</span>
      </div>
      <div ref={mathRef} className={styles.math} />
      <p className={styles.description}>{equation.description}</p>
      {variableValues.length > 0 && (
        <div className={styles.variables}>
          {variableValues.map(v => (
            <span key={v.id} className={styles.variable}>
              {v.id} = {formatValue(v.value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatValue(value: number | undefined): string {
  if (value === undefined) return '—';
  if (Math.abs(value) < 0.001 && value !== 0) {
    return value.toExponential(2);
  }
  if (Math.abs(value) > 10000) {
    return value.toExponential(2);
  }
  return value.toFixed(4);
}
